/**
 * Trailing-edge debounce with `cancel()` and `flush()`.
 * Used for the 350 ms search delay and the 4 s compose autosave.
 */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  /** Drop the pending call. */
  cancel(): void;
  /** Run the pending call now (if any). */
  flush(): void;
  /** Whether a call is pending. */
  pending(): boolean;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;

  const run = () => {
    timer = null;
    const args = lastArgs;
    lastArgs = null;
    if (args) fn(...args);
  };

  const wrapped = ((...args: A) => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, ms);
  }) as Debounced<A>;

  wrapped.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };
  wrapped.flush = () => {
    if (!timer) return;
    clearTimeout(timer);
    run();
  };
  wrapped.pending = () => timer !== null;
  return wrapped;
}
